import React, { useEffect, forwardRef, useImperativeHandle } from "react";
import CommunityProfileComponent from "./CommunityProfileComponent";
import DynamicFormEditableFields from "./DynamicFormEditableFields";
import useDynamicFormAPI from "./useDynamicFormAPI";
import { fieldsForMember } from '../../../utils/profileSides';

// onlyWrittenFields: just the written-answer questions (e.g. Bio) — used
// after the AI chat sign-up, where the tick-box ones were answered in the chat.
const DynamicFormMain = forwardRef(({ user, communityId, onCommunityName, setCommunityProfileComplete, mode, autoSaveOnBlur = false, onlyWrittenFields = false }, ref) => {
  const {
    json,
    formData,
    communityName,
    isComplete,
    incompleteFields,
    setFormData,
    submitUpdate,
    setHasUserChanges,
    checkCompletionNow,
    otherBios,
  } = useDynamicFormAPI(user, communityId, onCommunityName, mode);

  // Let the parent (e.g. the onboarding wizard) ask "are you actually
  // complete right now?" synchronously, bypassing the isComplete state's
  // render-lag — see checkCompletionNow in useDynamicFormAPI.
  useImperativeHandle(ref, () => ({ checkCompletionNow }));

  console.log('[communitydynamicprofile] === COMPONENT RENDER ===');
  console.log('[communitydynamicprofile] props:', { user: user?.userId, communityId, mode });
  console.log('[communitydynamicprofile] setCommunityProfileComplete function reference:', setCommunityProfileComplete.toString().substring(0, 100));
  console.log('[communitydynamicprofile] json:', json);
  console.log('[communitydynamicprofile] formData:', formData);
  console.log('[communitydynamicprofile] communityName:', communityName);
  console.log('[communitydynamicprofile] isComplete:', isComplete);
  console.log('[communitydynamicprofile] incompleteFields:', incompleteFields);

  // Determine data and schema keys based on mode
  const dataKey = mode === "Profile" ? "Profile" : "Settings";
  const schemaKey = mode === "Profile" ? "profileSchema" : "Settings";

  // Handle input changes
  const handleInputChange = (event, communityProfileName, fieldName) => {
    const value = event.target.value;
    console.log('[communitydynamicprofile] handleInputChange called');
    console.log('[communitydynamicprofile] fieldName:', fieldName);
    console.log('[communitydynamicprofile] value:', value);
    console.log('[communitydynamicprofile] dataKey:', dataKey);
    console.log('[communitydynamicprofile] communityProfileName:', communityProfileName);
    console.log('[communitydynamicprofile] current formData before update:', formData);
    
    setHasUserChanges(true); // Mark that user made changes
    
    setFormData(prevState => {
      const newState = {
        ...prevState,
        data: {
          ...prevState.data,
          [dataKey]: {
            ...(prevState.data[dataKey] || {}),
            [fieldName]: value,
          },
        },
      };
      console.log('[communitydynamicprofile] new formData state:', newState);
      return newState;
    });
  };

  // Handle boolean changes
  const handleBooleanChange = (event, communityProfileName, fieldName) => {
    const value = event.target.value; // Will be true/false for boolean
    setHasUserChanges(true); // Mark that user made changes
    
    setFormData(prevState => ({
      ...prevState,
      data: {
        ...prevState.data,
        [dataKey]: {
          ...(prevState.data[dataKey] || {}),
          [fieldName]: value,
        },
      },
    }));
  };

  // Store previous isComplete value to prevent unnecessary calls
  const [prevIsComplete, setPrevIsComplete] = React.useState(null);

  // Conditionally update completion status
  useEffect(() => {
    console.log('[communitydynamicprofile] useEffect setCommunityProfileComplete called');
    console.log('[communitydynamicprofile] isComplete:', isComplete);
    console.log('[communitydynamicprofile] prevIsComplete:', prevIsComplete);
    console.log('[communitydynamicprofile] setCommunityProfileComplete type:', typeof setCommunityProfileComplete);
    console.log('[communitydynamicprofile] setCommunityProfileComplete function reference changed:', setCommunityProfileComplete);
    console.log('[communitydynamicprofile] Current communityId:', communityId);
    console.log('[communitydynamicprofile] Current user:', user?.userId);
    console.log('[communitydynamicprofile] Current mode:', mode);
    
    if (typeof setCommunityProfileComplete === 'function' && isComplete !== prevIsComplete) {
      console.log('[communitydynamicprofile] About to call setCommunityProfileComplete with:', isComplete);
      setCommunityProfileComplete(isComplete);
      setPrevIsComplete(isComplete);
      console.log('[communitydynamicprofile] Called setCommunityProfileComplete successfully');
    } else if (isComplete === prevIsComplete) {
      console.log('[communitydynamicprofile] ✅ Skipping setCommunityProfileComplete call - value unchanged');
    }
  }, [isComplete, setCommunityProfileComplete, prevIsComplete]);

  // Handle checkbox changes
  const handleCheckboxChange = (event, communityProfileName, fieldName) => {
    const value = event.target.value;
    setHasUserChanges(true); // Mark that user made changes
    
    setFormData(prevState => ({
      ...prevState,
      data: {
        ...prevState.data,
        [dataKey]: {
          ...(prevState.data[dataKey] || {}),
          [fieldName]: value,
        },
      },
    }));
  };

  // Render fields
  const renderFieldElements = (fields, schema, data, communityProfileName, DisableFields) =>
    fields.map((fieldName) => {
      const fieldConfig = schema[fieldName];
      const fieldType = fieldConfig.type;
      const fieldEnum = fieldConfig.enum;
      console.log('mooode', mode)
      const fieldValue = data?.[mode]?.[fieldName] ?? (fieldType === "boolean" ? false : "");
      const characterLimit = fieldConfig.characterLimit;
      const characterMinimum =
        typeof fieldConfig.characterMinimum === "number"
          ? fieldConfig.characterMinimum
          : undefined;
      const placeHolder =
        typeof fieldConfig.placeHolder === "string"
          ? fieldConfig.placeHolder
          : "";
      
      // Extract additional schema variables
      const htmlTitle = fieldConfig.htmlTitle;
      const htmlSubheader = fieldConfig.htmlSubheader;
      const htmlSubheaderAlternative = fieldConfig.htmlSubheaderAlternative;
      const htmlref = fieldConfig.htmlref;

      // Determine which handler to use
      let handleSave;
      if (fieldType === "boolean") {
        handleSave = handleBooleanChange;
      } else if (
        Array.isArray(fieldType) &&
        fieldType[0]?.type === "String" &&
        fieldType[0]?.htmlref === "checkbox"
      ) {
        handleSave = handleCheckboxChange;
      } else {
        handleSave = handleInputChange;
      }

      return (
        <DynamicFormEditableFields
          key={fieldName}
          fieldName={fieldName}
          fieldValue={fieldValue}
          characterLimit={characterLimit}
          characterMinimum={characterMinimum}
          placeHolder={placeHolder}
          fieldType={fieldType}
          fieldEnum={fieldEnum}
          htmlTitle={htmlTitle}
          htmlSubheader={htmlSubheader}
          htmlSubheaderAlternative={htmlSubheaderAlternative}
          htmlref={htmlref}
          sliderConfig={htmlref === 'slider' ? fieldConfig : undefined}
          handleSave={handleSave}
          communityProfileName={communityProfileName}
          DisableFields={DisableFields}
          autoSaveOnBlur={autoSaveOnBlur}
          otherBios={fieldName === "Bio" ? otherBios : undefined}
        />
      );
    });

  // Render community profiles
  const renderCommunityProfiles = () => {
    if (!formData || !formData.schema || !formData.data) return null;
    
const { schema, data } = formData;

console.log('dyanmicformmain data',data)

    if (!schema || !data) return null;

    return Object.entries(schema).map(([communityProfileName, communityProfileSchema]) => {
      // Two-sided communities: only this member's side's questions (utils/profileSides.js).
      const allFields = mode === 'Profile' ? fieldsForMember(communityProfileSchema, data?.Profile) : Object.keys(communityProfileSchema);
      const fields = onlyWrittenFields
        ? allFields.filter((k) => communityProfileSchema[k]?.type === 'String' && !Array.isArray(communityProfileSchema[k]?.enum) && !communityProfileSchema[k]?.side)
        : allFields;
      const EditableFieldElements = renderFieldElements(fields, communityProfileSchema, data, communityProfileName, false);
      const NonEditableFieldElements = renderFieldElements(fields, communityProfileSchema, data, communityProfileName, true);

      return (
        <CommunityProfileComponent
          key={communityProfileName}
          communityProfileName={communityName}
          formattedCommunityProfileName={communityName}
          NonEditableFieldElements={NonEditableFieldElements}
          EditableFieldElements={EditableFieldElements}
          handleSubmitCommunityProfile={submitUpdate}
        />
      );
    });
  };

  return (
    <header>
      {/* <p>
        Information you share here will be only shown to {communityName} members
      </p> */}
      {isComplete ? (
        <div></div>
      ) : (
        <p>
          🟡 Please complete: {incompleteFields.join(", ")}
        </p>
      )}
      {formData && (
        <div>{renderCommunityProfiles()}</div>
      )}
    </header>
  );
});

export default DynamicFormMain;


// import React, { useEffect } from "react";
// import CommunityProfileComponent from "./CommunityProfileComponent";
// import EditableField from "./EditableFields";
// import useDynamicFormAPI from "./useDynamicFormAPI";

// const DynamicFormMain = ({ user, communityId, onCommunityName, setCommunityProfileComplete, mode }) => {
//   const {
//     json,
//     formData,
//     communityName,
//     isComplete,
//     incompleteFields,
//     setFormData,
//     submitUpdate
//   } = useDynamicFormAPI(user, communityId, onCommunityName, mode);

//   console.log('[DynamicFormMain] json:', json);
//   console.log('[DynamicFormMain] formData:', formData);
//   console.log('[DynamicFormMain] communityName:', communityName);
//   console.log('[DynamicFormMain] isComplete:', isComplete);
//   console.log('[DynamicFormMain] incompleteFields:', incompleteFields);

//   // Determine data and schema keys based on mode
//   const dataKey = mode === "profile" ? "Profile" : "Settings";
//   const schemaKey = mode === "profile" ? "profileSchema" : "Settings";

//   // Handle input changes
//   const handleInputChange = (event, communityProfileName, fieldName) => {
//     const value = event.target.value;
//     setFormData(prevState => ({
//       ...prevState,
//       data: {
//         ...prevState.data,
//         [dataKey]: {
//           ...(prevState.data[dataKey] || {}),
//           [fieldName]: value,
//         },
//       },
//     }));
//   };

//   // Conditionally update completion status
//   useEffect(() => {
//     if (typeof setCommunityProfileComplete === 'function') {
//       setCommunityProfileComplete(isComplete);
//     }
//   }, [isComplete, setCommunityProfileComplete]);

//   // Handle checkbox changes
//   const handleCheckboxChange = (event, communityProfileName, fieldName) => {
//     const value = event.target.value;
//     setFormData(prevState => ({
//       ...prevState,
//       data: {
//         ...prevState.data,
//         [dataKey]: {
//           ...(prevState.data[dataKey] || {}),
//           [fieldName]: value,
//         },
//       },
//     }));
//   };

//   // Render fields
//   const renderFieldElements = (fields, schema, data, communityProfileName, DisableFields) =>
//     fields.map((fieldName) => {
//       const fieldType = schema[fieldName].type;
//       const fieldEnum = schema[fieldName].enum;
//       const fieldValue = data?.[fieldName] ?? "";
//       const characterLimit = schema[fieldName].characterLimit;
//       const characterMinimum =
//         typeof schema[fieldName].characterMinimum === "number"
//           ? schema[fieldName].characterMinimum
//           : undefined;
//       const placeHolder =
//         typeof schema[fieldName].placeHolder === "string"
//           ? schema[fieldName].placeHolder
//           : "";

//       return (
//         <EditableField
//           key={fieldName}
//           fieldName={fieldName}
//           fieldValue={fieldValue}
//           characterLimit={characterLimit}
//           characterMinimum={characterMinimum}
//           placeHolder={placeHolder}
//           fieldType={fieldType}
//           fieldEnum={fieldEnum}
//           handleSave={
//             Array.isArray(fieldType) &&
//             fieldType[0]?.type === "String" &&
//             fieldType[0]?.htmlref === "checkbox"
//               ? handleCheckboxChange
//               : handleInputChange
//           }
//           communityProfileName={communityProfileName}
//           DisableFields={DisableFields}
//         />
//       );
//     });

//   // Render community profiles
//   const renderCommunityProfiles = () => {
//     if (!formData || !formData.schema || !formData.data) return null;
    

// const { schema, data } = formData;
//     if (!schema || !data) return null;

//     return Object.entries(schema).map(([communityProfileName, communityProfileSchema]) => {
//       const fields = Object.keys(communityProfileSchema);
//       const EditableFieldElements = renderFieldElements(fields, communityProfileSchema, data, communityProfileName, false);
//       const NonEditableFieldElements = renderFieldElements(fields, communityProfileSchema, data, communityProfileName, true);

//       return (
//         <CommunityProfileComponent
//           key={communityProfileName}
//           communityProfileName={communityName}
//           formattedCommunityProfileName={communityName}
//           NonEditableFieldElements={NonEditableFieldElements}
//           EditableFieldElements={EditableFieldElements}
//           handleSubmitCommunityProfile={submitUpdate}
//         />
//       );
//     });
//   };

//   return (
//     <header>
//       <p>
//         Information you share here will be only shown to {communityName} members
//       </p>
//       {isComplete ? (
//         <div></div>
//       ) : (
//         <p>
//           🟡 Please complete: {incompleteFields.join(", ")}
//         </p>
//       )}
//       {formData && (
//         <div>{renderCommunityProfiles()}</div>
//       )}
//     </header>
//   );
// };

// export default DynamicFormMain;





// import React, { useEffect } from "react";
// import CommunityProfileComponent from "./CommunityProfileComponent";
// import EditableField from "./EditableFields";
// import useDynamicFormAPI from "./useDynamicFormAPI";

// const DynamicFormMain = ({ user, communityId, onCommunityName, setCommunityProfileComplete, mode }) => {
//   const {
//     json,
//     formData,
//     communityName,
//     isComplete,
//     incompleteFields,
//     setFormData,
//     submitUpdate
//   } = useDynamicFormAPI(user, communityId, onCommunityName, mode);

// console.log('[DynamicFormMain] json:', json);
// console.log('[DynamicFormMain] formData:', formData);
// console.log('[DynamicFormMain] communityName:', communityName);
// console.log('[DynamicFormMain] isComplete:', isComplete);
// console.log('[DynamicFormMain] incompleteFields:', incompleteFields);



//   // Handle input changes
//   const handleInputChange = (event, communityProfileName, fieldName) => {
//     const value = event.target.value;
//     setFormData(prevState => ({
//       ...prevState,
//       data: {
//         ...prevState.data,
//         Profile: {
//           ...(prevState.data.Profile || {}),
//           [fieldName]: value,
//         },
//       },
//     }));
//   };

//   // Conditionally update completion status
//   useEffect(() => {
//     if (typeof setCommunityProfileComplete === 'function') {
//       setCommunityProfileComplete(isComplete);
//     }
//   }, [isComplete, setCommunityProfileComplete]);

//   // Handle checkbox changes
//   const handleCheckboxChange = (event, communityProfileName, fieldName) => {
//     const value = event.target.value;
//     setFormData(prevState => ({
//       ...prevState,
//       data: {
//         ...prevState.data,
//         Profile: {
//           ...(prevState.data.Profile || {}),
//           [fieldName]: value,
//         },
//       },
//     }));
//   };

//   // Render fields
//   const renderFieldElements = (fields, schema, data, communityProfileName, DisableFields) =>
//     fields.map((fieldName) => {
//       const fieldType = schema[fieldName].type;
//       const fieldEnum = schema[fieldName].enum;
//       const fieldValue = data.Profile?.[fieldName] ?? "";
//       const characterLimit = schema[fieldName].characterLimit;
//       const characterMinimum =
//         typeof schema[fieldName].characterMinimum === "number"
//           ? schema[fieldName].characterMinimum
//           : undefined;
//       const placeHolder =
//         typeof schema[fieldName].placeHolder === "string"
//           ? schema[fieldName].placeHolder
//           : "";

//       return (
//         <EditableField
//           key={fieldName}
//           fieldName={fieldName}
//           fieldValue={fieldValue}
//           characterLimit={characterLimit}
//           characterMinimum={characterMinimum}
//           placeHolder={placeHolder}
//           fieldType={fieldType}
//           fieldEnum={fieldEnum}
//           handleSave={
//             Array.isArray(fieldType) &&
//             fieldType[0].type === "String" &&
//             fieldType[0].htmlref === "checkbox"
//               ? handleCheckboxChange
//               : handleInputChange
//           }
//           communityProfileName={communityProfileName}
//           DisableFields={DisableFields}
//         />
//       );
//     });

//   // Render community profiles
//   const renderCommunityProfiles = () => {
//     if (!formData || !formData.schema || !formData.data) return null;
//     const { schema, data } = formData;
//     return Object.entries(schema).map(([communityProfileName, communityProfileSchema]) => {
//       const fields = Object.keys(communityProfileSchema);
//       const EditableFieldElements = renderFieldElements(fields, communityProfileSchema, data, communityProfileName, false);
//       const NonEditableFieldElements = renderFieldElements(fields, communityProfileSchema, data, communityProfileName, true);

//       return (
//         <CommunityProfileComponent
//           key={communityProfileName}
//           communityProfileName={communityName}
//           formattedCommunityProfileName={communityName}
//           NonEditableFieldElements={NonEditableFieldElements}
//           EditableFieldElements={EditableFieldElements}
//           handleSubmitCommunityProfile={submitUpdate}
//         />
//       );
//     });
//   };

//   return (
//     <header>
//       <p>
//         DfsYNAMIC FORM MAIN Information you share here will be only shown to {communityName} members
//       </p>
//       {isComplete ? (
//         <div></div>
//       ) : (
//         <p>
//           🟡 Please complete: {incompleteFields.join(", ")}
//         </p>
//       )}
//       {json && json.schemaProfile && json.userProfile && (
//         <div>{renderCommunityProfiles()}</div>
//       )}
//     </header>
//   );
// };

// export default DynamicFormMain;

// import React from "react";
// import { useEffect } from "react";
// import CommunityProfileComponent from "./CommunityProfileComponent";
// import EditableField from "./EditableFields";
// import useCommunityProfileAPI from "./useCommunityProfileAPI";

// /** 
//  * CommunityProfileMain
//  * 
//  * Main container for rendering and editing a user's community profile.
//  * Handles form state, rendering, and delegates all API/data logic to useCommunityProfileAPI.
//  * 
//  * Props:
//  * - user: Object (current user info)
//  * - communityId: String
//  * - onCommunityName: Function
//  */
// const CommunityProfileMain = ({ user, communityId, onCommunityName, setCommunityProfileComplete }) => {
//   const {
//     json,
//     formData,
//     communityName,
//     isCommunityProfileComplete,
//     incompleteFields,
//     setFormData,
//     submitProfileUpdate
//   } = useCommunityProfileAPI(user, communityId, onCommunityName);

//   // Handles all input changes (text, textarea, select, checkbox)
//   const handleInputChange = (event, communityProfileName, fieldName) => {
//     const value = event.target.value;
//     setFormData((prevState) => ({
//       ...prevState,
//       data: {
//         ...prevState.data,
//         Profile: {
//           ...(prevState.data.Profile || {}),
//           [fieldName]: value,
//         },
//       },
//     }));
//   };

//     useEffect(() => {
//     const communityProfileCompleteCheck = async () => {
//      setCommunityProfileComplete(isCommunityProfileComplete)
//     };
//     console.log('setCommunityProfileComplete communitymainf', communityProfileCompleteCheck)

//     communityProfileCompleteCheck();
//   }, [isCommunityProfileComplete]);

//   // Handles checkbox group changes (for EditableField's synthetic event)
//   const handleCheckboxChange = (event, communityProfileName, fieldName) => {
//     const value = event.target.value;
//     setFormData((prevState) => ({
//       ...prevState,
//       data: {
//         ...prevState.data,
//         Profile: {
//           ...(prevState.data.Profile || {}),
//           [fieldName]: value,
//         },
//       },
//     }));
//   };

//   // Renders all fields for a given profile schema section using refactored EditableField
//   const renderFieldElements = (fields, schema, data, communityProfileName, DisableFields) =>
//     fields.map((fieldName) => {
//       const fieldType = schema[fieldName].type;
//       const fieldEnum = schema[fieldName].enum;
//       const fieldValue = data.Profile?.[fieldName] ?? "";
//       const characterLimit = schema[fieldName].characterLimit;
//       const characterMinimum =
//         typeof schema[fieldName].characterMinimum === "number"
//           ? schema[fieldName].characterMinimum
//           : undefined;
//       // Extract placeHolder if present, else undefined
//       const placeHolder =
//         typeof schema[fieldName].placeHolder === "string"
//           ? schema[fieldName].placeHolder
//           : "";

//       return (
//         <EditableField
//           key={fieldName}
//           fieldName={fieldName}
//           fieldValue={fieldValue}
//           characterLimit={characterLimit}
//           characterMinimum={characterMinimum}
//           placeHolder={placeHolder}
//           fieldType={fieldType}
//           fieldEnum={fieldEnum}
//           handleSave={
//             Array.isArray(fieldType) &&
//             fieldType[0].type === "String" &&
//             fieldType[0].htmlref === "checkbox"
//               ? handleCheckboxChange
//               : handleInputChange
//           }
//           communityProfileName={communityProfileName}
//           DisableFields={DisableFields}
//         />
//       );
//     });



//   // Renders all community profile sections
//   const renderCommunityProfiles = () => {
//     if (!formData || !formData.schema || !formData.data) return null;
//     const { schema, data } = formData;
//     console.log('[CommunityProfileMain.js] formData:', formData)
//     return Object.entries(schema).map(([communityProfileName, communityProfileSchema]) => {
//       const fields = Object.keys(communityProfileSchema);
//       const EditableFieldElements = renderFieldElements(fields, communityProfileSchema, data, communityProfileName, false);
//       const NonEditableFieldElements = renderFieldElements(fields, communityProfileSchema, data, communityProfileName, true);

//       return (
//         <CommunityProfileComponent
//           key={communityProfileName}
//           communityProfileName={communityName}
//           formattedCommunityProfileName={communityName}
//           NonEditableFieldElements={NonEditableFieldElements}
//           EditableFieldElements={EditableFieldElements}
//           handleSubmitCommunityProfile={submitProfileUpdate}
//         />
//       );
//     });
//   };

//   return (
//     <header>
//       <p>
//         Information you share here will be only shown to {communityName} members
//       </p>
//      {isCommunityProfileComplete ? (

// <div></div>
// ) : (
//   <>

//     <p>
// 🟡 Please complete: {incompleteFields.join(', ')}
//     </p>
//   </>
// )}

//       {json && json.schemaProfile && json.userProfile && (
//         <div>{renderCommunityProfiles()}</div>
//       )}
//     </header>
//   );
// };

// export default CommunityProfileMain;
